import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router } from "@angular/router";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { AuthenticationService } from "src/app/services/authentication.service";
import { StormwaterRoleEnum } from "src/app/shared/generated/enum/stormwater-role-enum";
import { RoadSegmentService } from "src/app/shared/generated/api/road-segment.service";
import { RoadSegmentAssessmentService } from "src/app/shared/generated/api/road-segment-assessment.service";

@Injectable({ providedIn: "root" })
export class StormwaterManageRoadSegmentAccessGuard implements CanActivate {
    constructor(
        private authenticationService: AuthenticationService,
        private roadSegmentService: RoadSegmentService,
        private roadSegmentAssessmentService: RoadSegmentAssessmentService,
        private router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot) {
        const roadSegmentIDParam = Number(route.params["roadSegmentID"]);
        const assessmentIDParam = Number(route.params["roadSegmentAssessmentID"]);

        const segmentID$: Observable<number | null> = Number.isFinite(roadSegmentIDParam)
            ? of(roadSegmentIDParam)
            : Number.isFinite(assessmentIDParam)
                ? this.roadSegmentAssessmentService.getRoadSegmentAssessment(assessmentIDParam).pipe(
                      map((a) => a?.RoadSegmentID ?? null),
                      catchError(() => of(null))
                  )
                : of(null);

        return segmentID$.pipe(
            switchMap((segmentID) => {
                if (segmentID == null) {
                    this.router.navigate(["/"]);
                    return of(false);
                }
                return this.checkAccess(segmentID);
            })
        );
    }

    private checkAccess(roadSegmentID: number) {
        return forkJoin({
            user: this.authenticationService.getCurrentUser().pipe(take(1)),
            segment: this.roadSegmentService.getRoadSegment(roadSegmentID).pipe(catchError(() => of(null))),
        }).pipe(
            map(({ user, segment }) => {
                // Mirrors legacy Person.CanManageStormwaterJurisdiction: requires an active
                // stormwater role (Admin or Normal — not Unassigned) AND membership in the
                // segment's jurisdiction. Admin does NOT bypass the jurisdiction check.
                const role = user?.StormwaterRoleID;
                const hasStormwaterRole =
                    role === StormwaterRoleEnum.Admin || role === StormwaterRoleEnum.Normal;
                const jurisdictionID = segment?.StormwaterJurisdiction?.StormwaterJurisdictionID;
                const managesJurisdiction =
                    jurisdictionID != null && (user?.ManagedStormwaterJurisdictionIDs ?? []).includes(jurisdictionID);

                if (hasStormwaterRole && managesJurisdiction) {
                    return true;
                }

                this.router.navigate(["/"]);
                return false;
            })
        );
    }
}
